import React, { useState, useEffect } from 'react';
import ResearchLayout from '../../components/ResearchLayout';
import { protocolService } from '../../services/protocolService';
import { useAuth } from '../../contexts/AuthContext';
import AIAssessmentViewer from "../../components/AIAssessmentViewer";

const ProgramAdminCreateProtocol = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    show_ai_response: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [userProtocols, setUserProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load user's protocols on component mount
  useEffect(() => {
    loadUserProtocols();
  }, []);

  const loadUserProtocols = async () => {
    try {
      setLoading(true);
  const response = await protocolService.getProtocols({ per_page: 10000 });
      setUserProtocols(response.protocols || []);
    } catch (error) {
      console.error('Error loading protocols:', error);
      setError('Failed to load protocols');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Create protocol using API
      const response = await protocolService.createProtocol({
        name: formData.name.trim(),
        description: formData.description.trim(),
        show_ai_response: formData.show_ai_response
      });

      if (response.success) {
        // Show success notification
        setShowSuccess(true);
        setFormData({ name: '', description: '' });
        
        // Reload user protocols
        await loadUserProtocols();

        // Hide success message after 3 seconds
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        throw new Error(response.message || 'Failed to create protocol');
      }

    } catch (error) {
      console.error('Error creating protocol:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create protocol. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ResearchLayout pageTitle="Create Protocol">
      <div className="container-fluid">
        {/* Success Alert */}
        {showSuccess && (
          <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            <strong>Success!</strong> Protocol created successfully.
            <button type="button" className="btn-close" onClick={() => setShowSuccess(false)}></button>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-exclamation-circle me-2"></i>
            <strong>Error!</strong> {error}
            <button type="button" className="btn-close" onClick={() => setError('')}></button>
          </div>
        )}

        <div className="row justify-content-center">
          <div className="col-lg-8 col-md-10">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-warning text-white">
                <h5 className="mb-0">
                  <i className="fas fa-plus-circle me-2"></i>
                  Create New Research Protocol
                </h5>
              </div>
              <div className="card-body p-4">
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label htmlFor="name" className="form-label fw-bold">
                      <i className="fas fa-clipboard-list me-2 text-warning"></i>
                      Protocol Name *
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter protocol name (e.g., Cardiovascular Health Assessment)"
                      required
                    />
                    <div className="form-text">
                      Choose a clear, descriptive name for your research protocol.
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="description" className="form-label fw-bold">
                      <i className="fas fa-align-left me-2 text-warning"></i>
                      Protocol Description *
                    </label>
                    <textarea
                      className="form-control"
                      id="description"
                      name="description"
                      rows="5"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Provide a detailed description of the protocol, its objectives, and methodology..."
                      required
                    ></textarea>
                    <div className="form-text">
                      Describe the purpose, scope, and methodology of this research protocol.
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="show_ai_response" className="form-label fw-bold">
                      <i className="fas fa-robot me-2 text-warning"></i>
                      Evaluation Results Visibility *
                    </label>
                    <select
                      className="form-select"
                      id="show_ai_response"
                      name="show_ai_response"
                      value={formData.show_ai_response ? 'true' : 'false'}
                      onChange={e => setFormData(prev => ({ ...prev, show_ai_response: e.target.value === 'true' }))}
                      required
                    >
                      <option value="true">View Evaluation Results (Doctors will see Evaluation Results)</option>
                      <option value="false">Hide Evaluation Results (Doctors will NOT see Evaluation Results)</option>
                    </select>
                    <div className="form-text">
                      Choose whether doctors can view the Evaluation Results when answering questions in this protocol.
                    </div>
                  </div>
                  <div className="bg-light p-3 rounded mb-4">
                    <h6 className="text-warning mb-3">
                      <i className="fas fa-info-circle me-2"></i>
                      Protocol Guidelines
                    </h6>
                    <ul className="mb-0 small">
                      <li>Use clear, professional language for the protocol name</li>
                      <li>Include specific research objectives in the description</li>
                      <li>Mention target participant demographics if applicable</li>
                      <li>Describe expected outcomes or measurements</li>
                      <li>After creating, you can add questions and link them to this protocol</li>
                    </ul>
                  </div>

                  <div className="d-flex justify-content-between align-items-center">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setFormData({ name: '', description: '' })}
                      disabled={isSubmitting}
                    >
                      <i className="fas fa-undo me-2"></i>
                      Reset Form
                    </button>
                    
                    <button
                      type="submit"
                      className="btn btn-warning btn-lg px-4"
                      disabled={isSubmitting || !formData.name.trim() || !formData.description.trim()}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Creating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Create Protocol
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Existing Protocols Preview */}
        <div className="row mt-5">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light">
                <h6 className="mb-0">
                  <i className="fas fa-list me-2 text-warning"></i>
                  My Created Protocols ({userProtocols.length})
                </h6>
              </div>
              <div className="card-body">
                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-warning" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-2">Loading protocols...</p>
                  </div>
                ) : userProtocols.length > 0 ? (
                  <div className="row">
                    {userProtocols.map((protocol) => (
                      <div key={protocol.id} className="col-md-6 mb-3">
                        <div className="card border border-warning">
                          <div className="card-body">
                            <h6 className="card-title text-warning">{protocol.name}</h6>
                            <p className="card-text text-muted small">
                              {protocol.description && protocol.description.length > 100
                                ? `${protocol.description.substring(0, 100)}...`
                                : protocol.description
                              }
                            </p>
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <small className="text-muted d-block">
                                  <i className="fas fa-user me-1"></i>
                                  Created by: {protocol.created_by_name || 'Unknown'}
                                </small>
                                <small className="text-muted">
                                  <i className="fas fa-calendar-alt me-1"></i>
                                  {protocol.created_at ? new Date(protocol.created_at).toLocaleDateString() : 'N/A'}
                                </small>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <span className={`badge ${protocol.show_ai_response ? 'bg-info' : 'bg-secondary'}`}
                                  title={protocol.show_ai_response ? 'Doctors can view AI assessment' : 'Doctors cannot view AI assessment'}>
                                  <i className="fas fa-robot me-1"></i>
                                  {protocol.show_ai_response ? 'AI Viewed' : 'AI Hidden'}
                                </span>
                                <span className="badge bg-success">
                                  {protocol.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>
                            <div className="mt-2">
                              <small className="text-info">
                                <i className="fas fa-question-circle me-1"></i>
                                {protocol.question_count || 0} Questions
                              </small>
                            </div>
                            {/* Conditionally display the AI Assessment component */}
                            {protocol.show_ai_response && protocol.aiResponseData && (
                              <div className="mt-3">
                                <AIAssessmentViewer data={protocol.aiResponseData} />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
                    <h6 className="text-muted">No Protocols Created Yet</h6>
                    <p className="text-muted">Create your first research protocol using the form above.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ResearchLayout>
  );
};

export default ProgramAdminCreateProtocol;